import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  timeout: 15000,
});

export async function fetchState() {
  const { data } = await apiClient.get("/state");
  return data;
}

export async function fetchPromisesByAudience() {
  const { data } = await apiClient.get("/promises/by-audience");
  return data;
}

export async function fetchLedger(limit = 12) {
  const { data } = await apiClient.get("/ledger", { params: { limit } });
  return data;
}

export async function fetchWorkbooks() {
  const { data } = await apiClient.get("/workbooks");
  return data;
}

export async function fetchWorkbookRaw(id) {
  // Fetch the raw markdown AND parsed-question structure in parallel — the
  // workbook detail page needs both: the markdown for the article render
  // (with inline pick widgets injected per cycle-5c+25-fix7), and the
  // parsed structure for the bottom submit bar.
  const [rawRes, parsedRes] = await Promise.all([
    apiClient.get(`/workbooks/${id}/raw`),
    apiClient.get(`/workbooks/${id}/parsed`),
  ]);
  return { markdown: rawRes.data, parsed: parsedRes.data };
}

export async function fetchAgents() {
  const { data } = await apiClient.get("/agents");
  return data;
}

export async function fetchFederation() {
  const { data } = await apiClient.get("/federation/v1");
  return data;
}

export async function fetchDriftRisk() {
  const { data } = await apiClient.get("/federation/drift-risk");
  return data;
}

export async function fetchPerAppRoadmaps() {
  const { data } = await apiClient.get("/federation/per-app-roadmaps");
  return data;
}

export async function fetchOwnerDecisions(latestOnly = true) {
  const { data } = await apiClient.get("/owner/decisions", { params: { latest_only: latestOnly } });
  return data;
}

export async function fetchDecisionQuestions(itemType, itemId, sourcePath = "") {
  const params = sourcePath ? { source_path: sourcePath } : undefined;
  const { data } = await apiClient.get(
    `/owner/decision-questions/${encodeURIComponent(itemType)}/${encodeURIComponent(itemId)}`,
    { params },
  );
  return data;
}

export async function submitOwnerDecision(payload, token) {
  const { data } = await apiClient.post("/owner/decide", payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

// ── Phase B-2 cycle-5c+25-fix13: prior workbook-pick read-back ────────────

/**
 * Map { workbook_id → {submitted_at, submitted_by, ack_path, accept_all} }
 * for every workbook the Owner has previously submitted picks on. Used by
 * the unified roadmap list to mark those workbooks as `status="decided"`
 * so they auto-hide alongside per-card decided rows.
 */
export async function fetchPicksByWorkbook() {
  try {
    const { data } = await apiClient.get("/picks/by-workbook");
    return data || {};
  } catch {
    return {};
  }
}

/**
 * Full prior-pick record for one workbook (latest ack only). Returns null
 * if no prior submission exists (the endpoint returns 404 in that case).
 *
 * Shape: { workbook_id, ack_path, submitted_at, submitted_by, accept_all,
 *          picks: [{q_num, letter, option_text, free_text}, ...] }
 */
export async function fetchLastPickAck(wbId) {
  try {
    const { data } = await apiClient.get(`/picks/last/${encodeURIComponent(wbId)}`);
    return data;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}


// ── Phase 14 / Batch 3 / cycle-5c+25-fix20: view-audit + work-audit ──────
//
// Owner-ratified picks 2026-05-25 (Q1=a derived; Q2=b explicit-only;
// Q3=d tiered; Q4=c badge+sort+filter):
//   - Work-audit (canon-derived) = latest Owner decision OR workbook
//     pick-ack timestamp per (item_type, item_id).
//   - View-audit (Mongo)       = explicit "I worked on this" click per
//     authenticated Owner. Auto-opens DO NOT touch.

/**
 * Map { "<item_type>:<item_id>" → {last_worked_at, source} } derived from
 * canon. No auth required (read-only canon-derived data).
 */
export async function fetchWorkAuditByItem() {
  try {
    const { data } = await apiClient.get("/work-audit/by-item");
    return data || {};
  } catch {
    return {};
  }
}

/**
 * Map { "<item_type>:<item_id>" → {last_viewed_at, viewer_email} } from
 * Mongo view_audit collection. No auth required to READ; only the write
 * path is gated. Empty map on first load / if collection is empty.
 */
export async function fetchViewAuditByItem() {
  try {
    const { data } = await apiClient.get("/view-audit/by-item");
    return data || {};
  } catch {
    return {};
  }
}

/**
 * Record an explicit "I worked on this" touch for a roadmap item. Auth
 * required. Surfaces backend 401/403/503 errors to the caller.
 */
export async function submitViewAuditTouch(itemType, itemId, token) {
  const { data } = await apiClient.post(
    "/view-audit/touch",
    { item_type: itemType, item_id: itemId },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return data;
}

/**
 * Revert (delete) the latest view-audit touch by the CURRENT viewer for
 * the given item. Auth required. cycle-5c+25-fix25 — supports the inverse
 * "No work done" affordance for accidental opens / non-engagement.
 *
 * Returns { ok, removed: 0|1, new_latest_at: <iso|null> }.
 */
export async function revertViewAuditTouch(itemType, itemId, token) {
  const { data } = await apiClient.delete(
    `/view-audit/touch?item_type=${encodeURIComponent(itemType)}&item_id=${encodeURIComponent(itemId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return data;
}

// ── Phase 15 / cycle-5c+25-fix21 / ADR-MP-005: MP cycle-inbox ───────────────
//
// Owner-direct request 2026-05-25: MP must be made aware when the Owner
// submits a card. Cycle-inbox aggregates "new Owner activity since MP last
// acknowledged" so MP can read it at cycle start AND the Owner can see
// (via the dashboard banner) whether MP is caught up.

/**
 * Aggregated map of Owner submissions newer than the MP-cycle-state marker.
 * Shape: { marker_last_acknowledged_at, marker_default_used, new_decisions,
 *          new_pick_acks, new_view_touches, total_new }
 */
export async function fetchCycleInbox() {
  try {
    const { data } = await apiClient.get("/mp/cycle-inbox");
    return data || null;
  } catch {
    return null;
  }
}

/**
 * Bump the MP cycle-state marker to NOW. Auth-required (Owner clicks
 * "Mark MP caught up" after MP confirms in chat that it read the new
 * submissions). Returns { ok, marker_last_acknowledged_at, acknowledged_by }.
 */
export async function acknowledgeCycleInbox(token) {
  const { data } = await apiClient.post(
    "/mp/cycle-acknowledge",
    {},
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return data;
}

